import React, { useState, useEffect } from 'react';
import './Absences.css';
import { useNavigate } from 'react-router-dom';
import { absencesAPI } from '../services/api';
import toast from 'react-hot-toast';
import { Plus, Calendar, CheckCircle, Clock, XCircle, AlertCircle, Eye, Pencil } from 'lucide-react';

const TYPE_LABELS = {
  CONGE_PAYE:       'Congé payé',
  CONGE_SANS_SOLDE: 'Congé sans solde',
  RTT:              'RTT',
};

const STATUT_META = {
  BROUILLON: { label: 'Brouillon', cls: 'abs-badge--brouillon', icon: <AlertCircle size={12}/> },
  SOUMIS:    { label: 'Soumis',    cls: 'abs-badge--soumis',    icon: <Clock size={12}/> },
  VALIDE:    { label: 'Validé',    cls: 'abs-badge--valide',    icon: <CheckCircle size={12}/> },
  REFUSE:    { label: 'Refusé',    cls: 'abs-badge--refuse',    icon: <XCircle size={12}/> },
  ANNULE:    { label: 'Annulé',    cls: 'abs-badge--annule',    icon: <XCircle size={12}/> },
};

const fmt = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export default function Absences() {
  const navigate = useNavigate();
  const [balance, setBalance]   = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading]   = useState(true);
  const year = new Date().getFullYear();

  const load = () => {
    setLoading(true);
    Promise.all([
      absencesAPI.balance(year),
      absencesAPI.list({ annee: year }),
    ])
      .then(([b, r]) => { setBalance(b.data); setRequests(r.data || []); })
      .catch(() => toast.error('Erreur chargement'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleCancel = async (id) => {
    if (!window.confirm('Annuler cette demande ?')) return;
    try {
      await absencesAPI.cancel(id);
      toast.success('Demande annulée');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const upcoming = requests.filter(r =>
    ['SOUMIS', 'VALIDE'].includes(r.statut) && new Date(r.date_debut) >= new Date()
  ).sort((a, b) => new Date(a.date_debut) - new Date(b.date_debut));

  if (loading) return <div className="loading-state"><div className="spinner"/></div>;

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h1 style={{ margin: 0 }}>Mes absences</h1>
          <p style={{ margin: 0 }}>Gestion de vos congés et RTT — {year}</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-primary" onClick={() => navigate('/absences/new')}>
            <Plus size={16}/> Nouvelle demande
          </button>
        </div>
      </div>

      {/* Balance */}
      {balance && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '0.95rem', fontWeight: 600 }}>
            Solde de congés {year}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
            <BalanceCard title="Congés payés" data={balance.cp} color="var(--primary)"/>
            <BalanceCard title="RTT" data={balance.rtt} color="#8b5cf6"/>
            {(balance.css.pris > 0 || balance.css.soumis > 0) && (
              <div className="abs-balance-card" style={{ borderColor: '#f59e0b' }}>
                <div className="abs-balance-title" style={{ color: '#f59e0b' }}>Congé sans solde</div>
                <div className="abs-balance-row"><span>Pris</span><span>{balance.css.pris} j</span></div>
                <div className="abs-balance-row"><span>En attente</span><span>{balance.css.soumis} j</span></div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Upcoming */}
      {upcoming.length > 0 && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '0.95rem', fontWeight: 600 }}>
            <Calendar size={16} style={{ verticalAlign: 'middle', marginRight: 6 }}/>
            Congés à venir
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {upcoming.map(r => {
              const sm = STATUT_META[r.statut] || STATUT_META.BROUILLON;
              return (
                <div key={r.id} className="abs-upcoming-row">
                  <span className={`abs-badge ${sm.cls}`}>{sm.icon} {sm.label}</span>
                  <span style={{ fontWeight: 500 }}>{TYPE_LABELS[r.type_conge]}</span>
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                    {fmt(r.date_debut)} → {fmt(r.date_fin)}
                  </span>
                  <span style={{ fontWeight: 600, color: 'var(--primary)' }}>{r.nb_jours} j</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* All requests */}
      <div className="card">
        <h3 style={{ margin: '0 0 16px', fontSize: '0.95rem', fontWeight: 600 }}>
          Historique des demandes {year}
        </h3>
        {requests.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)' }}>
            Aucune demande cette année
          </div>
        ) : (
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Type</th>
                <th>Début</th>
                <th>Fin</th>
                <th>Jours</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map(r => {
                const sm = STATUT_META[r.statut] || STATUT_META.BROUILLON;
                return (
                  <tr key={r.id}>
                    <td>{TYPE_LABELS[r.type_conge]}</td>
                    <td>{fmt(r.date_debut)}</td>
                    <td>{fmt(r.date_fin)}</td>
                    <td style={{ fontWeight: 600 }}>{r.nb_jours}</td>
                    <td>
                      <span className={`abs-badge ${sm.cls}`}>{sm.icon} {sm.label}</span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                        <button
                          className="btn btn-ghost btn-sm"
                          title="Consulter"
                          onClick={() => navigate(`/absences/${r.id}`)}
                        >
                          <Eye size={13}/>
                        </button>
                        {r.statut === 'BROUILLON' && (
                          <button
                            className="btn btn-ghost btn-sm"
                            title="Modifier"
                            onClick={() => navigate(`/absences/${r.id}/edit`)}
                          >
                            <Pencil size={13}/>
                          </button>
                        )}
                        {['BROUILLON', 'SOUMIS'].includes(r.statut) && (
                          <button
                            className="btn btn-ghost btn-sm"
                            style={{ color: '#f59e0b' }}
                            onClick={() => handleCancel(r.id)}
                          >
                            <XCircle size={13}/> Annuler
                          </button>
                        )}
                        {r.commentaire_rh && (
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                            💬 {r.commentaire_rh}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function BalanceCard({ title, data, color }) {
  return (
    <div className="abs-balance-card" style={{ borderColor: color }}>
      <div className="abs-balance-title" style={{ color }}>{title}</div>
      <div className="abs-balance-row"><span>Acquis</span><strong>{data.acquis} j</strong></div>
      <div className="abs-balance-row"><span>Pris</span><span>{data.pris} j</span></div>
      <div className="abs-balance-row"><span>En attente</span><span style={{ color: '#f59e0b' }}>{data.soumis} j</span></div>
      <div className="abs-balance-divider"/>
      <div className="abs-balance-row"><span>Restants</span><strong style={{ color }}>{data.restants} j</strong></div>
      <div className="abs-balance-row" style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
        <span>Prévus après demandes</span><span>{data.prevus} j</span>
      </div>
    </div>
  );
}
